import cx from 'classnames';
import { useEffect, useRef } from 'react';
import type { VoidFunctionComponent } from 'react';

import Button from '../generic/Button/Button';
import LabeledCheckbox from '../generic/Forms/LabeledCheckbox';
import SearchInput from '../generic/Forms/SearchInput';
import { icons } from '../generic/Icon/icons';
import List from '../generic/List';
import { TruncatedTooltip } from '../generic/TruncatedTooltip/TruncatedTooltip';
import { ProjectIcon } from '../projects/dashboardV2/ProjectsTable/ProjectItem/ProjectTitle';
import type { TextInputOnChange } from '../generic/Forms/InputBase';

import { LEGAL_AGENT_SCOPE_MAX } from './legalAgentQuery';
import styles from './LegalAgentScopeList.module.less';
import type { ScopeOption } from './LegalAgentScopeSelect';

type Noun = 'project' | 'release group';

const NOUNS: Record<Noun, { plural: string; capitalPlural: string }> = {
  project: { plural: 'projects', capitalPlural: 'Projects' },
  'release group': { plural: 'release groups', capitalPlural: 'Release groups' },
};

/**
 * The parts of the results' infinite query that the list needs. The result of `useInfiniteQuery` can be passed as is.
 */
export type ScopeQuery = {
  hasNextPage?: boolean;
  isFetching: boolean;
  isError: boolean;
  fetchNextPage: () => unknown;
  refetch: () => unknown;
};

export type LegalAgentScopeListProps = {
  noun: Noun;
  search: string;
  onSearchChange: TextInputOnChange;
  selected: ScopeOption[];
  //  The loaded results for the search; selected ones are filtered out here.
  results: ScopeOption[];
  //  Matches for the search, and every entity of the kind; null until known.
  matchCount: number | null;
  totalCount: number | null;
  query: ScopeQuery;
  //  The results shown may be out of date: a newer search is pending, or the current one is refetching.
  isRefreshing: boolean;
  onToggle: (option: ScopeOption, isChecked: boolean) => void;
  onClear: () => void;
  //  Adds every match for the search, not only the loaded pages.
  onSelectAllMatching?: () => void;
  isSelectingAll?: boolean;
};

//  A toggled row moves to the other section once the new selection renders; focus waits for that move.
type PendingFocus = { value: string; isChecked: boolean; fallback: string | null };

const LOADING_ROW_KEYS = ['1', '2', '3'];
const ROW_SELECTOR = '[data-test-id="legal-agent-scope-row"]';

const ScopeRow: VoidFunctionComponent<{
  option: ScopeOption;
  isChecked: boolean;
  isDisabled?: boolean;
  onToggle: LegalAgentScopeListProps['onToggle'];
}> = ({ option, isChecked, isDisabled, onToggle }) => (
  <div className={styles.row} data-test-id="legal-agent-scope-row" data-scope-value={option.value}>
    <LabeledCheckbox
      className={styles.checkbox}
      label={<TruncatedTooltip id={`legal-agent-scope-name-${option.value}`} text={option.label} placement="top" className={styles.name} />}
      isChecked={isChecked}
      isDisabled={isDisabled}
      isRippleDisabled
      onChange={(_event, checked) => onToggle(option, checked)}
    />
    <span className={styles.icons}>
      <span className={styles.iconSlot}>{option.isPrivate && <ProjectIcon projectTypeKey="private" />}</span>
      <span className={styles.iconSlot}>{option.type && <ProjectIcon projectTypeKey={option.type} />}</span>
    </span>
  </div>
);

export const LegalAgentScopeList: VoidFunctionComponent<LegalAgentScopeListProps> = ({
  noun,
  search,
  onSearchChange,
  selected,
  results,
  matchCount,
  totalCount,
  query,
  isRefreshing,
  onToggle,
  onClear,
  onSelectAllMatching,
  isSelectingAll = false,
}) => {
  const { hasNextPage, isFetching, isError, fetchNextPage, refetch } = query;
  //  A failed query still reports a next page, so stop on errors or the same page would be requested forever.
  const canFetchNextPage = Boolean(hasNextPage) && !isFetching && !isError;
  //  A failed query keeps saying it failed while it is being retried, so wait for the retry before saying so again.
  const hasFailed = isError && !isFetching;
  const hasRows = results.length > 0;
  const isLoading = !hasRows && isFetching && !isRefreshing;
  const { plural, capitalPlural } = NOUNS[noun];
  const rootRef = useRef<HTMLDivElement>(null);
  const pendingFocus = useRef<PendingFocus | null>(null);
  const selectedIds = new Set(selected.map((option) => option.value));
  const unselected = results.filter((option) => !selectedIds.has(option.value));
  //  Unloaded pages may still hold unselected matches, so only a fully loaded, fully selected list rules them out.
  const isEveryMatchSelected = !hasNextPage && unselected.length === 0;
  //  While refreshing, matchCount belongs to the previous search, so neither the button nor the hint can use it.
  const hasCurrentMatchCount = onSelectAllMatching !== undefined && !isRefreshing && !hasFailed && matchCount !== null;
  const isAtScopeMax = selected.length >= LEGAL_AGENT_SCOPE_MAX;
  //  Matches may include selected ones, so this can hide an add that would fit; counting exactly needs every match loaded.
  const fitsScopeMax = hasCurrentMatchCount && selected.length + matchCount <= LEGAL_AGENT_SCOPE_MAX;
  const canSelectAllMatching = fitsScopeMax && matchCount > 1 && !isEveryMatchSelected;
  const isOverSelectAllMax = hasCurrentMatchCount && !fitsScopeMax && !isAtScopeMax && !isEveryMatchSelected;
  const resultsLabel = search === '' ? `All ${plural}` : `Matching ${plural}`;

  useEffect(() => {
    const pending = pendingFocus.current;
    const root = rootRef.current;
    if (!pending || !root) {
      return;
    }
    const rows = [...root.querySelectorAll<HTMLElement>(ROW_SELECTOR)];
    const inputFor = (value: string | null) =>
      rows.find((row) => row.dataset.scopeValue === value)?.querySelector<HTMLInputElement>('input') ?? null;
    const moved = inputFor(pending.value);
    if (moved && moved.checked !== pending.isChecked) {
      return;
    }
    pendingFocus.current = null;
    (moved ?? inputFor(pending.fallback) ?? root.querySelector<HTMLInputElement>('input[type="search"]'))?.focus();
  });

  const clearSelection = () => {
    pendingFocus.current = null;
    onClear();
    rootRef.current?.querySelector<HTMLInputElement>('input[type="search"]')?.focus();
  };

  const toggleFrom = (sectionValues: string[]): LegalAgentScopeListProps['onToggle'] => (option, isChecked) => {
    const index = sectionValues.indexOf(option.value);
    pendingFocus.current = {
      value: option.value,
      isChecked,
      fallback: sectionValues[index + 1] ?? sectionValues[index - 1] ?? null,
    };
    onToggle(option, isChecked);
  };
  const onToggleSelected = toggleFrom(selected.map((option) => option.value));
  const onToggleResult = toggleFrom(unselected.map((option) => option.value));

  return (
    <div className={styles.scopeList} data-test-id="legal-agent-scope-list" ref={rootRef}>
      <SearchInput
        label={`Search ${plural}`}
        value={search}
        onChange={onSearchChange}
        placeholder={`Search ${plural}`}
        size="small"
        testId="legal-agent-scope-search"
      />
      <div className={styles.filterRow}>
        {/* Inert until the filters flyout exists; Button requires a handler. */}
        <Button type="text" size="small" icon={icons.filter} isDisabled onClick={() => undefined}>Filters</Button>
        {matchCount !== null && totalCount !== null && (
          <span className={styles.count}>{`${matchCount} of ${totalCount}`}</span>
        )}
      </div>
      <span className="sr-only" aria-live="polite" data-test-id="legal-agent-scope-announcement">
        {`${selected.length} selected.`}
        {matchCount !== null && totalCount !== null && ` ${matchCount} of ${totalCount} ${plural}.`}
      </span>
      {selected.length > 0 && (
        <section className={styles.section} aria-label={`Selected ${plural}`}>
          <div className={styles.sectionHeader}>
            <span className={styles.sectionLabel}>{`Selected · ${selected.length}`}</span>
            <Button type="text" size="small" onClick={clearSelection}>Clear</Button>
          </div>
          {isAtScopeMax && <span className={styles.hint}>{`You can select up to ${LEGAL_AGENT_SCOPE_MAX} ${plural}.`}</span>}
          {selected.map((option) => (
            <ScopeRow key={option.value} option={option} isChecked onToggle={onToggleSelected} />
          ))}
        </section>
      )}
      <section
        className={cx(styles.section, { [styles.refreshing]: isRefreshing })}
        aria-label={resultsLabel}
        aria-busy={isRefreshing}
      >
        <span className={styles.sectionLabel}>{search === '' ? `All ${plural}` : 'Matching'}</span>
        {canSelectAllMatching && (
          <Button type="text" size="small" isDisabled={isSelectingAll} onClick={onSelectAllMatching}>
            {isSelectingAll ? 'Selecting...' : `Select ${matchCount} matching`}
          </Button>
        )}
        {isOverSelectAllMax && (
          <span className={styles.hint}>
            {`You can select up to ${LEGAL_AGENT_SCOPE_MAX}. Narrow the search to select them all.`}
          </span>
        )}
        <div className={styles.rows}>
          {hasFailed && (
            <div className={styles.message} role="alert">
              {hasRows ? "Couldn't load more." : `${capitalPlural} couldn't be loaded.`}
              <Button type="text" size="small" onClick={() => { void (hasRows ? fetchNextPage() : refetch()); }}>
                Try again
              </Button>
            </div>
          )}
          {isLoading && (
            <List>
              <List.Body>
                {LOADING_ROW_KEYS.map((key) => <List.RowLoading key={key} isInteractive={false} />)}
              </List.Body>
            </List>
          )}
          {!isLoading && !hasFailed && !hasNextPage && unselected.length === 0 && (
            <output className={styles.message}>
              {search === '' ? `No other ${plural}.` : `No ${plural} match.`}
            </output>
          )}
          {!isLoading && unselected.map((option) => (
            <ScopeRow key={option.value} option={option} isChecked={false} isDisabled={isAtScopeMax} onToggle={onToggleResult} />
          ))}
          {hasNextPage && !hasFailed && (
            <Button
              type="outline"
              size="small"
              isDisabled={!canFetchNextPage || isRefreshing}
              onClick={() => { void fetchNextPage(); }}
            >
              Load more
            </Button>
          )}
        </div>
      </section>
    </div>
  );
};
